import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import {
  alpha,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Snackbar,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { forward as toMGRS, toPoint } from "mgrs";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";

import MissionDetailsPanel from "../components/fdc/MissionDetailsPanel";
import MetVePanel from "../components/fdc/MetVePanel";
import FireControlBar from "../components/fdc/FireControlBar";
import FiringDataPanel from "../components/fdc/FiringDataPanel";
import ManualObservationPanel from "../components/fdc/ManualObservationPanel";
import UnprocessedObservationsPanel from "../components/fdc/UnprocessedObservationsPanel";
import ProcessedObservationsPanel from "../components/fdc/ProcessedObservationsPanel";
import AdminPanel from "../components/fdc/AdminPanel";
import ToolsDock from "../components/fdc/ToolsDock";
import LmMissionPanel from "../components/fdc/LmMissionPanel";
import MapComponent from "../components/map/MapComponent";
// import ChatPanel from "../components/fdc/ChatPanel"; // hidden per user request
import {
  DEFAULT_EXPANDED,
  getMissionOpenState,
  parsePtl,
  buildMgrsString,
  extractEffectiveTargetMgrs,
  deriveObservationMetrics,
} from "../utils/fdcHelpers";
import { GUN_OPTIONS, getChargeOptions, BACKEND_URL } from "../utils/constants";
import useToolPanels from "../hooks/useToolPanels";
import usePatternConfig from "../hooks/usePatternConfig";
import useDockResize from "../hooks/useDockResize";
import { useError } from "../contexts/ErrorContext";

export default function FdcPage() {
  const { showError } = useError();
  const backendUrl = BACKEND_URL;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const tacticalButtonSx = {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: 0.3,
    borderWidth: 1.5,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 0 0 1px rgba(166,176,120,0.08), inset 0 1px 0 rgba(255,255,255,0.03)"
        : "0 2px 8px rgba(27,35,20,0.10)",
  };

  const tacticalContainedSx = {
    ...tacticalButtonSx,
    color: theme.palette.mode === "dark" ? "#11150f" : "#f7f7f2",
    bgcolor: theme.palette.mode === "dark" ? "#a6b078" : "#556b2f",
    "&:hover": {
      bgcolor: theme.palette.mode === "dark" ? "#b7c58a" : "#465a27",
    },
    "&.Mui-disabled": {
      color: theme.palette.text.disabled,
      bgcolor: alpha(theme.palette.action.disabledBackground, 0.9),
      boxShadow: "none",
    },
  };

  const tacticalOutlinedSx = {
    ...tacticalButtonSx,
    color: theme.palette.mode === "dark" ? "#c7d1a7" : "#465a27",
    borderColor:
      theme.palette.mode === "dark"
        ? "rgba(166,176,120,0.55)"
        : "rgba(85,107,47,0.55)",
    bgcolor:
      theme.palette.mode === "dark"
        ? "rgba(41,52,31,0.28)"
        : "rgba(125,141,88,0.08)",
    "&:hover": {
      borderColor: theme.palette.mode === "dark" ? "#a6b078" : "#556b2f",
      bgcolor:
        theme.palette.mode === "dark"
          ? "rgba(63,79,47,0.40)"
          : "rgba(125,141,88,0.16)",
    },
    "&.Mui-disabled": {
      borderColor: theme.palette.divider,
      color: theme.palette.text.disabled,
    },
  };

  // ── Form state ──
  const [form, setForm] = useState({
    mission_id: "",
    description: "",
    bty_center: { x: 0, y: 0, z: 0 },
    ptl: "",
    target: { x: 0, y: 0, z: 0 },
    charge: "3wb",
    weapon: "M109",
    mgrs_target: "",
    mgrs_zone: "29SND",
    bty_altitude: 0,
    target_altitude: 0,
    call_for_fire: {},
    // MET / VE corrections (optional; blank = standard). Used by the MT4-306 solver.
    angle: "la",
    mv_ve: "",
    propellant_temp_c: "",
    proj_squares: "",
    wind_dir_mil: "",
    wind_speed_kn: "",
    air_temp_pct: "",
    air_density_pct: "",
  });
  const hasMission = Boolean(form.mission_id);

  // ── Mission / data state ──
  const [missionOptions, setMissionOptions] = useState([]);
  const [missionNotification, setMissionNotification] = useState(false);
  const [newObservationNotification, setNewObservationNotification] = useState(false);
  const previousMissionOptionsRef = useRef([]);
  const hasLoadedMissionsRef = useRef(false);
  const [ptlError, setPtlError] = useState("");
  const [observations, setObservations] = useState([]);
  const [processedObservations, setProcessedObservations] = useState([]);
  const [shots, setShots] = useState([]);
  const [selectedShotId, setSelectedShotId] = useState(null);
  const [selectedProcessedObservationId, setSelectedProcessedObservationId] = useState(null);
  const [selectedGunId, setSelectedGunId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batteryOptions, setBatteryOptions] = useState([]);
  const [latestGunPositions, setLatestGunPositions] = useState([]);
  const [registryWeapons, setRegistryWeapons] = useState([]);
  const [selectedMissionGunNumbers, setSelectedMissionGunNumbers] = useState([]);
  const [committedMissionGunNumbers, setCommittedMissionGunNumbers] = useState([]);
  const [assigningMissionGuns, setAssigningMissionGuns] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [mgrsError, setMgrsError] = useState({ target: "", battery: "" });
  const [selectedObservationGuns, setSelectedObservationGuns] = useState({});
  const [correctionMode, setCorrectionMode] = useState("single");
  const [regulatingGunId, setRegulatingGunId] = useState(null);
  const [effectiveTargetMgrs, setEffectiveTargetMgrs] = useState("");
  const [expanded, setExpanded] = useState(DEFAULT_EXPANDED);
  const [safetyClearances, setSafetyClearances] = useState([]);
  const [fuzeType, setFuzeType] = useState("PD");
  const [heightOfBurst, setHeightOfBurst] = useState(20);
  const [projectile, setProjectile] = useState("HE");
  const [rounds, setRounds] = useState(1);
  const [phase, setPhase] = useState("AF"); // AF (Adjust Fire) — only reg gun fires; FFE — all committed guns fire
  const [activeObservationId, setActiveObservationId] = useState(null);
  const transientFocusTimeoutRef = useRef(null);

  // One-shot map pan to a gun: sets selection briefly, then clears it so the highlight doesn't stick.
  const focusGunTransiently = useCallback((gunId) => {
    setSelectedGunId(gunId);
    if (transientFocusTimeoutRef.current) clearTimeout(transientFocusTimeoutRef.current);
    transientFocusTimeoutRef.current = setTimeout(() => {
      setSelectedGunId(null);
      transientFocusTimeoutRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => () => {
    if (transientFocusTimeoutRef.current) clearTimeout(transientFocusTimeoutRef.current);
  }, []);

  // ── Extracted hooks ──
  const tools = useToolPanels(hasMission, { autoOpenMap: !isMobile });
  const pattern = usePatternConfig();
  const dock = useDockResize({
    isMobile,
    deps: [missionNotification, hasMission, effectiveTargetMgrs, tools.toolsOpen],
  });

  const getMissionPtlStorageKey = (missionId) => `chacal-ptl-${missionId}`;

  // ── Panel handlers ──
  const openPanel = (panel) =>
    setExpanded((prev) => ({ ...prev, [panel]: true }));

  const handleExpand = (panel) => (_event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // ── Validation ──
  const validatePtl = () => {
    const n = parsePtl(form.ptl);
    if (n === null) { setPtlError("PTL is mandatory."); return false; }
    if (n < 0 || n > 6400) { setPtlError("PTL must be between 0 and 6400 mils."); return false; }
    setPtlError("");
    return true;
  };

  const getCenterTargetMgrs = () =>
    pattern.patternAnchor === "effective" && effectiveTargetMgrs
      ? effectiveTargetMgrs
      : form.mgrs_target;

  // ── Data fetching ──
  const fetchMissionState = async (missionId) => {
    if (!missionId) return;
    try {
      const res = await axios.get(`${backendUrl}/mission-state/${missionId}`, { timeout: 10000 });
      const mgrs = res.data?.data?.effective_mgrs_target;
      if (mgrs) {
        setEffectiveTargetMgrs(String(mgrs));
        pattern.setPatternAnchor("effective");
        try {
          const [longitude, latitude] = toPoint(String(mgrs));
          setMapCenter({ lat: latitude, lng: longitude });
        } catch (err) {
          console.warn("Could not convert effective mgrs to lat/long:", err);
        }
      } else {
        setEffectiveTargetMgrs("");
        pattern.setPatternAnchor("original");
      }
    } catch (err) {
      console.error("Failed to fetch mission state:", err);
      setEffectiveTargetMgrs("");
      pattern.setPatternAnchor("original");
    }
  };

  const fetchObservations = async () => {
    if (!form.mission_id) return;
    try {
      const response = await axios.get(`${backendUrl}/unprocessed-observations/${form.mission_id}`);
      if (response.data.status === "success") setObservations(response.data.data);
    } catch (error) {
      console.error("Error fetching observations:", error);
    }
  };

  const fetchSafetyClearances = async () => {
    if (!form.mission_id) { setSafetyClearances([]); return; }
    try {
      const res = await axios.get(`${backendUrl}/safety-clearances/${encodeURIComponent(form.mission_id)}`);
      setSafetyClearances(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Error fetching safety clearances:", err);
    }
  };

  const fetchProcessedObservations = async () => {
    if (!form.mission_id) return;
    try {
      const response = await axios.get(`${backendUrl}/processed-observations/${form.mission_id}`);
      if (response.data.status === "success") setProcessedObservations(response.data.data);
    } catch (error) {
      console.error("Error fetching processed observations:", error);
    }
  };

  const fetchShots = async () => {
    if (!form.mission_id) return;
    try {
      const response = await axios.get(`${backendUrl}/shots/${form.mission_id}`);
      setShots(response.data);
    } catch (error) {
      console.error("Error fetching shots:", error);
    }
  };

  const fetchBatteryData = async () => {
    if (!form.mission_id) return [];
    try {
      const response = await axios.get(`${backendUrl}/battery-data/${form.mission_id}`, { timeout: 15000 });
      const incoming = response.data?.data;
      if (!Array.isArray(incoming)) return [];

      const mapped = incoming.map((option) => ({
        ...option,
        id: String(option.gun_number),
        mgrs: toMGRS([parseFloat(option.wests), parseFloat(option.norths)]),
        is_battery_center: true,
      }));

      const missionGunNumbers = mapped.map((g) => String(g.gun_number));
      setBatteryOptions(mapped);
      setCommittedMissionGunNumbers(missionGunNumbers);
      return mapped;
    } catch (error) {
      console.error("Error fetching battery data (keeping old list):", error);
      return [];
    }
  };

  const fetchLatestGunPositions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/latest-gun-positions`, { timeout: 15000 });
      const incoming = Array.isArray(response.data?.data) ? response.data.data : [];
      const byGun = new Map(incoming.map((item) => [String(item.gun_number), item]));

      const normalized = GUN_OPTIONS.map((gun) => {
        const existing = byGun.get(String(gun.value));
        if (!existing) {
          return {
            id: String(gun.value), gun_number: String(gun.value),
            hasPosition: false, norths: null, wests: null, altitude: null, mgrs: "", updated_at: null,
          };
        }
        return {
          ...existing,
          id: String(existing.gun_number), gun_number: String(existing.gun_number),
          hasPosition: true,
          mgrs: toMGRS([parseFloat(existing.wests), parseFloat(existing.norths)]),
        };
      });

      setLatestGunPositions(normalized);
      return normalized;
    } catch (error) {
      console.error("Error fetching latest gun positions:", error);
      return [];
    }
  };

  // ── Gun selection handlers ──
  const toggleObservationGun = (obsId, gunId) => {
    setSelectedObservationGuns((prev) => {
      const oldList = prev[obsId] || [];
      const newList = oldList.includes(gunId) ? oldList.filter((id) => id !== gunId) : [...oldList, gunId];
      return { ...prev, [obsId]: newList };
    });
  };

  const toggleGunForMission = (id) => {
    const gunId = String(id);
    setSelectedMissionGunNumbers((prev) =>
      prev.includes(gunId) ? prev.filter((item) => item !== gunId) : [...prev, gunId].slice(0, 6),
    );
  };

  const selectAllGunsForMission = () =>
    setSelectedMissionGunNumbers(latestGunPositions.filter((g) => g.hasPosition).slice(0, 6).map((g) => String(g.gun_number)));

  const clearAllGunsForMission = () => setSelectedMissionGunNumbers([]);

  const selectAllGunsForObservation = (obsId) =>
    setSelectedObservationGuns((prev) => ({ ...prev, [obsId]: batteryOptions.map((g) => g.id) }));

  const clearAllGunsForObservation = (obsId) =>
    setSelectedObservationGuns((prev) => ({ ...prev, [obsId]: [] }));

  // ── Mission battery sync ──
  const syncMissionBatteryFromLatest = useCallback(
    async (gunNumbers, { silent = false } = {}) => {
      if (!form.mission_id) return false;
      const normalizedGuns = Array.isArray(gunNumbers)
        ? Array.from(new Set(gunNumbers.map((gun) => String(gun ?? "").trim()).filter(Boolean))).slice(0, 6)
        : [];
      try {
        await axios.post(`${backendUrl}/battery-data/from-latest`, { mission_id: form.mission_id, gun_numbers: normalizedGuns }, { timeout: 19000 });
        return true;
      } catch (error) {
        console.error("Failed to sync selected guns from latest positions:", error);
        if (!silent) alert(error.response?.data?.message || error.message || "Failed to sync guns from latest saved positions.");
        return false;
      }
    },
    [backendUrl, form.mission_id],
  );

  const handleAssignSelectedGunsToMission = useCallback(async () => {
    if (!form.mission_id) { alert("Select a mission first."); return false; }
    const selectableGunNumbers = latestGunPositions
      .filter((gun) => selectedMissionGunNumbers.includes(String(gun.gun_number)) && gun.hasPosition)
      .map((gun) => String(gun.gun_number));

    // Submission is additive: send the UNION of committed + selected so deselecting an
    // on-mission gun never removes it. Only "more guns to add" triggers the submit
    // button in the bar, and that's exactly what this call does.
    const unionGunNumbers = Array.from(new Set([
      ...committedMissionGunNumbers.map(String),
      ...selectableGunNumbers,
    ]));

    setAssigningMissionGuns(true);
    try {
      const synced = await syncMissionBatteryFromLatest(unionGunNumbers);
      if (!synced) return false;
      await fetchBatteryData();
      setCommittedMissionGunNumbers(unionGunNumbers);
      return true;
    } finally {
      setAssigningMissionGuns(false);
    }
  }, [form.mission_id, latestGunPositions, selectedMissionGunNumbers, committedMissionGunNumbers, syncMissionBatteryFromLatest]);

  // ── Observation handling ──
  const handleManualObservationSubmitted = async () => {
    await fetchObservations();
    setExpanded((prev) => ({ ...prev, manualObs: true, unproc: true }));
  };

  const removeObservation = async (observationId, shotId) => {
    if (!form.mission_id) return;
    const ok = window.confirm(`Remove observation ${shotId}? This cannot be undone.`);
    if (!ok) return;
    try {
      const response = await axios.delete(`${backendUrl}/observations/${form.mission_id}/${observationId}`, { timeout: 10000 });
      if (response.data?.status !== "success") throw new Error(response.data?.message || "Failed to remove observation");
      // Refresh both queues — the obs may have been in either.
      await Promise.all([fetchObservations(), fetchProcessedObservations()]);
      if (activeObservationId === observationId) setActiveObservationId(null);
    } catch (error) {
      console.error("Error removing observation:", error);
      alert(error.response?.data?.message || error.message || "Failed to remove observation.");
    }
  };

  const processObservation = async (observationId, shotId) => {
    if (!String(form.ptl ?? "").trim()) {
      setPtlError("PTL is mandatory.");
      alert("Please fill PTL before processing observations.");
      openPanel("missionDetails");
      return;
    }
    if (!validatePtl()) {
      alert("Please fill PTL with a valid value before processing observations.");
      openPanel("missionDetails");
      return;
    }

    setLoading(true);

    // Always use the TOP FireControlBar gun selection. The observation correction is
    // applied via the regulating gun (which produces the effective target); every
    // other selected gun gets a fire element aimed at that effective target.
    const gunNumbers = selectedMissionGunNumbers
      .map((n) => String(n).trim())
      .filter(Boolean);
    if (!gunNumbers.length) {
      alert("Select at least one gun in the Fire Control bar.");
      setLoading(false);
      return;
    }

    const regulatingGun = batteryOptions.find((g) => g.id === regulatingGunId);
    const regulatingGunNumber = regulatingGun ? String(regulatingGun.gun_number).trim() : "";

    // The regulating gun must be in the gun_ids list so the backend processes it first
    // and uses its corrected target as the effective target for the rest.
    if (regulatingGunNumber && !gunNumbers.includes(regulatingGunNumber)) {
      gunNumbers.unshift(regulatingGunNumber);
    }

    const guns = {};
    for (const gunNumber of gunNumbers) {
      const gun = batteryOptions.find((g) => String(g.gun_number) === gunNumber);
      if (!gun) continue;
      guns[gunNumber] = { mgrs_bty_center: gun.mgrs, bty_altitude: gun.altitude };
    }

    try {
      const response = await axios.post(`${backendUrl}/process-observation-batch`, {
        observation_id: observationId, mission_id: form.mission_id, shot_id: shotId,
        gun_ids: gunNumbers, regulating_gun_id: regulatingGunNumber || null,
        weapon: form.weapon, mgrs_target: form.mgrs_target, charge: form.charge,
        target_altitude: form.target_altitude, ptl: Number(form.ptl), guns,
        fuze_type: fuzeType,
        projectile,
        height_of_burst: fuzeType === "TIME" ? heightOfBurst : 0,
        rounds,
        phase,
        angle: form.angle,
        mv_ve: form.mv_ve, propellant_temp_c: form.propellant_temp_c, proj_squares: form.proj_squares,
        wind_dir_mil: form.wind_dir_mil, wind_speed_kn: form.wind_speed_kn,
        air_temp_pct: form.air_temp_pct, air_density_pct: form.air_density_pct,
      });

      const payload = response.data || {};
      let effectiveCandidate = "";
      const missionState = payload.mission_state?.data ?? payload.mission_state;
      if (missionState?.effective_mgrs_target) effectiveCandidate = missionState.effective_mgrs_target;
      else if (payload.effective_mgrs_target) effectiveCandidate = payload.effective_mgrs_target;
      else {
        const byGun = payload.results_by_gun || {};
        if (regulatingGunNumber && byGun[regulatingGunNumber]) effectiveCandidate = extractEffectiveTargetMgrs(byGun[regulatingGunNumber]);
      }

      await fetchProcessedObservations();
      await fetchObservations();
      await fetchShots();
      if (activeObservationId === observationId) setActiveObservationId(null);
      if (effectiveCandidate) { setEffectiveTargetMgrs(effectiveCandidate); pattern.setPatternAnchor("effective"); }
      setExpanded((prev) => ({ ...prev, shots: true }));
    } catch (error) {
      console.error("Error processing observation batch:", error);
      // Structured backend error → re-fire showError with an action handler
      // that knows local context (mission id, observation id, gun selection).
      // The auto-fired dialog from the axios interceptor gets overridden by
      // this call so the buttons are now wired to real behaviour.
      if (error?.response?.data?.error_kind) {
        const missionId = form.mission_id;
        // Flip is_processed = TRUE on the observation without running the
        // adjust-fire calc. Preserves the audit trail — the observation
        // moves from the unprocessed panel into the processed panel rather
        // than being deleted, so BDA / after-action records stay complete.
        // Existing fire elements in the shots panel are untouched.
        const acknowledgeObservation = async () => {
          try {
            await axios.post(
              `${backendUrl}/observations/${missionId}/${observationId}/mark-processed`,
            );
          } catch (e) {
            console.error("Failed to mark observation as processed:", e);
          }
          await Promise.all([
            fetchObservations(),
            fetchProcessedObservations(),
            fetchShots(),
          ]);
          if (activeObservationId === observationId) setActiveObservationId(null);
        };

        showError(error, {
          onAction: async (action) => {
            if (action === "repeat" || action === "cancel") {
              // Round on target (repeat) or operator dismissed (cancel):
              // mark the observation as acknowledged. The last shot's fire
              // elements remain in the FiringDataPanel — operator can
              // confirm and fire again with the same elements.
              await acknowledgeObservation();
            } else if (action === "ffe") {
              // Round on target → switch the mission to Fire For Effect.
              // All committed guns will fire on the next confirmed shot.
              // Same fire elements remain visible.
              setPhase("FFE");
              setExpanded((prev) => ({ ...prev, shots: true }));
              await acknowledgeObservation();
            }
          },
        });
      } else {
        alert(error?.response?.data?.message || "Failed to process observation (server error).");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Mission submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePtl()) { alert("Please fill PTL (Rumo de Vigilância) before starting the mission."); return; }

    const selectedGuns = latestGunPositions.filter((opt) => selectedMissionGunNumbers.includes(String(opt.gun_number)));
    if (selectedGuns.length === 0) { alert("No guns selected for starting mission!"); return; }

    const synced = await handleAssignSelectedGunsToMission();
    if (!synced) return;

    let targetsByGun = null;
    if (pattern.patternEnabled) {
      pattern.setPatternError("");
      const res = pattern.computeTargetsByGun(selectedGuns, getCenterTargetMgrs());
      if (!res.ok) { pattern.setPatternError(res.error); alert(res.error); return; }
      targetsByGun = res.targets;
    }

    for (const gun of selectedGuns) {
      const gunIdStr = String(gun.gun_number);
      const updatedForm = {
        ...form, ptl: Number(form.ptl),
        bty_center: { x: parseFloat(gun.wests), y: parseFloat(gun.norths), z: parseFloat(gun.altitude) },
        bty_altitude: parseFloat(gun.altitude), gun_id: gun.gun_number, mgrs_bty_center: gun.mgrs,
        mgrs_target: pattern.patternEnabled && targetsByGun ? targetsByGun[gunIdStr] : form.mgrs_target,
        fuze_type: fuzeType,
        projectile,
        height_of_burst: fuzeType === "TIME" ? heightOfBurst : 0,
        rounds,
        phase,
      };
      try {
        await axios.post(`${backendUrl}/start_mission`, updatedForm, { timeout: 19000 });
      } catch (error) {
        console.error(`Error starting mission for gun ${gun.gun_number}`, error);
      }
    }

    await fetchShots();
    await fetchObservations();
    setExpanded((prev) => ({ ...prev, shots: true, unproc: true, proc: true }));
  };

  // ── Unified Fire dispatcher (Initial fire OR Adjust from active obs) ──
  const handleFire = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (activeObservationId) {
      const activeObs = observations.find((o) => o.id === activeObservationId);
      if (!activeObs) {
        // Stale active obs id - clear and fall through to initial fire
        setActiveObservationId(null);
        return handleSubmit(e || { preventDefault: () => {} });
      }
      return processObservation(activeObs.id, activeObs.shot_id);
    }
    return handleSubmit(e || { preventDefault: () => {} });
  };

  // ── Confirm / delete shots (FDC preview → send to guns) ──
  const confirmShots = async (shotIds) => {
    if (!form.mission_id) return;
    try {
      await axios.post(`${backendUrl}/confirm-shots`, {
        mission_id: form.mission_id,
        shot_ids: shotIds || null,
      });
      await fetchShots();
    } catch (error) {
      console.error("Error confirming shots:", error);
      alert("Failed to confirm fire elements.");
    }
  };

  const deleteShots = async (shotIds) => {
    if (!form.mission_id || !shotIds?.length) return;
    const ok = window.confirm(`Delete ${shotIds.length} pending fire element(s)?`);
    if (!ok) return;
    try {
      await axios.post(`${backendUrl}/delete-shots`, {
        mission_id: form.mission_id,
        shot_ids: shotIds,
      });
      await fetchShots();
    } catch (error) {
      console.error("Error deleting shots:", error);
      alert("Failed to delete fire elements.");
    }
  };

  // ── Mission change / clear ──
  const handleMissionChange = (e) => {
    const missionId = e.target.value;
    const savedPtl = missionId ? localStorage.getItem(getMissionPtlStorageKey(missionId)) ?? "" : "";
    setForm((prev) => ({ ...prev, mission_id: missionId, description: "", mgrs_target: "", ptl: savedPtl, target_altitude: 0, target: { x: 0, y: 0, z: 0 }, call_for_fire: {} }));
  };

  const refreshMissionOptions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/fetch-data`, { timeout: 15000 });
      if (response.data?.length > 0) {
        const missions = response.data.map((item) => ({ mission_id: item[1], description: `${item[10]} - ${item[11]}` }));
        const currentMissionIds = new Set(missions.map((m) => m.mission_id));
        const previousMissionIds = new Set(previousMissionOptionsRef.current.map((m) => m.mission_id));
        const hasNewMission = [...currentMissionIds].some((id) => !previousMissionIds.has(id));

        if (!hasLoadedMissionsRef.current) {
          previousMissionOptionsRef.current = missions;
          hasLoadedMissionsRef.current = true;
        } else if (hasNewMission) {
          setMissionNotification(true);
          previousMissionOptionsRef.current = missions;
        } else {
          previousMissionOptionsRef.current = missions;
        }
        setMissionOptions(missions);
      } else {
        previousMissionOptionsRef.current = [];
        setMissionOptions([]);
      }
    } catch (error) {
      console.error("Error fetching missions:", error);
    }
  };

  const handleMissionCreated = async ({ missionId, payload, parsedMgrs }) => {
    await refreshMissionOptions();
    const roundedAltitude = Math.round(Number(payload?.altitude || 0));
    setForm((prev) => ({
      ...prev, mission_id: missionId,
      description: `${payload?.target_number || missionId} - ${payload?.target_description || ""}`,
      mgrs_target: `${parsedMgrs.zone}${String(parsedMgrs.e).padStart(5, "0")}${String(parsedMgrs.n).padStart(5, "0")}`,
      target_altitude: roundedAltitude,
      target: { x: parsedMgrs.e, y: parsedMgrs.n, z: roundedAltitude },
      call_for_fire: {
        WarningOrder: payload?.warning_order, MethodOfTargetLocation: payload?.method_target_location,
        TargetName: payload?.target_number || missionId, TargetDescription: payload?.target_description,
        DegreeOfProtection: payload?.degree_of_protection, VerticalShot: payload?.tiro_vertical ? "Yes" : "No",
        Projectile: payload?.projectile, Fuze: payload?.fuze, MethodOfFire: payload?.method_of_fire,
        Distribution: payload?.distribution, MethodOfControl: payload?.method_of_control,
      },
    }));

    setShots([]); setObservations([]); setProcessedObservations([]); setBatteryOptions([]);
    setSelectedMissionGunNumbers([]); setCommittedMissionGunNumbers([]);
    setSelectedShotId(null); setSelectedProcessedObservationId(null); setSelectedGunId(null);
    setSelectedObservationGuns({}); setEffectiveTargetMgrs(""); pattern.resetPattern();
    tools.closeAllTools();
    setExpanded({ ...getMissionOpenState(true), missionDetails: true, startMission: true });
  };

  const handleMissionUpdated = async ({ missionId, payload, parsedMgrs }) => {
    await refreshMissionOptions();
    const roundedAltitude = Math.round(Number(payload?.altitude || 0));
    setForm((prev) => ({
      ...prev, mission_id: missionId,
      description: `${payload?.target_number || missionId} - ${payload?.target_description || ""}`,
      mgrs_target: `${parsedMgrs.zone}${String(parsedMgrs.e).padStart(5, "0")}${String(parsedMgrs.n).padStart(5, "0")}`,
      target_altitude: roundedAltitude,
      target: { x: parsedMgrs.e, y: parsedMgrs.n, z: roundedAltitude },
      call_for_fire: {
        ...prev.call_for_fire,
        WarningOrder: payload?.warning_order, MethodOfTargetLocation: payload?.method_target_location,
        TargetName: payload?.target_number || missionId, TargetDescription: payload?.target_description,
        DegreeOfProtection: payload?.degree_of_protection, VerticalShot: payload?.tiro_vertical ? "Yes" : "No",
        Projectile: payload?.projectile, Fuze: payload?.fuze, MethodOfFire: payload?.method_of_fire,
        Distribution: payload?.distribution, MethodOfControl: payload?.method_of_control,
      },
    }));
    tools.closeAllTools();
    setExpanded((prev) => ({ ...prev, missionDetails: true, startMission: true }));
    alert(`Mission ${missionId} updated successfully.`);
  };

  const handleClearMission = () => {
    localStorage.removeItem("chacal-selected-mission");
    setForm((prev) => ({ ...prev, mission_id: "", description: "", mgrs_target: "", ptl: "", target_altitude: 0, target: { x: 0, y: 0, z: 0 }, call_for_fire: {} }));
    setShots([]); setObservations([]); setProcessedObservations([]); setBatteryOptions([]);
    setSelectedShotId(null); setSelectedProcessedObservationId(null); setSelectedGunId(null);
    setSelectedObservationGuns({}); setEffectiveTargetMgrs(""); pattern.resetPattern();
    setMapCenter(null); tools.closeAllTools();
    setExpanded(getMissionOpenState(false));
  };

  const handleDeleteMission = async () => {
    if (!form.mission_id) return;
    const missionId = form.mission_id;
    const ok = window.confirm(
      `Delete mission "${missionId}"?\n\n` +
      "This permanently removes the call-for-fire record and every shot, observation, " +
      "and gun assignment tied to this mission.\nThis cannot be undone.",
    );
    if (!ok) return;
    try {
      const response = await axios.delete(`${backendUrl}/missions/${encodeURIComponent(missionId)}`, { timeout: 15000 });
      if (response.data?.status !== "success") {
        throw new Error(response.data?.message || "Failed to delete mission.");
      }
      // Clear local mission-scoped state and the localStorage hint.
      handleClearMission();
      // Refresh the dropdown so the deleted mission disappears.
      await refreshMissionOptions();
      alert(`Mission ${missionId} deleted.`);
    } catch (err) {
      console.error("Failed to delete mission:", err);
      alert(err.response?.data?.message || err.message || "Failed to delete mission.");
    }
  };

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleMgrsConfirm = (type) => {
    const mgrsRegex = /^[0-9]{2}[A-Z]{3}[0-9]{4,10}$/;
    const mgrsValue = type === "target" ? form.mgrs_target : "";
    if (!mgrsRegex.test(mgrsValue)) {
      setMgrsError((prev) => ({ ...prev, [type]: "Invalid Coordinates. Example: 29SND6000050000" }));
      return;
    }
    try {
      const [longitude, latitude] = toPoint(mgrsValue);
      if (type === "target") {
        setForm((prev) => ({ ...prev, target: { x: longitude, y: latitude, z: parseFloat(prev.target_altitude) } }));
        setMgrsError((prev) => ({ ...prev, target: "" }));
        setEffectiveTargetMgrs("");
        pattern.setPatternAnchor("original");
      }
    } catch {
      setMgrsError((prev) => ({ ...prev, [type]: "Error converting MGRS coordinates." }));
    }
  };

  const handleFetchMissionData = async () => {
    if (!form.mission_id) return;
    try {
      const response = await axios.get(`${backendUrl}/fetch-data`, { timeout: 16000 });
      const missionData = response.data.find((item) => item[1] === form.mission_id);
      if (missionData) {
        const mgrsCoords = buildMgrsString(form.mgrs_zone, missionData[6], missionData[7]);
        const roundedAltitude = Math.round(parseFloat(missionData[8]));
        setForm((prev) => ({
          ...prev,
          description: `${missionData[10]} - ${missionData[11]} - ${missionData[12]} - ${missionData[14]} - ${missionData[15]} - ${missionData[16]} - ${missionData[17]} - ${missionData[18]} - ${missionData[19]}`,
          mgrs_target: mgrsCoords, target_altitude: roundedAltitude,
          target: { x: parseFloat(missionData[7]), y: parseFloat(missionData[6]), z: roundedAltitude },
          call_for_fire: {
            WarningOrder: missionData[4], MethodOfTargetLocation: missionData[5],
            TargetName: missionData[10], TargetDescription: missionData[11],
            DegreeOfProtection: missionData[12], VerticalShot: missionData[13] ? "Yes" : "No",
            Projectile: missionData[14], Fuze: missionData[15], MethodOfFire: missionData[16],
            Distribution: missionData[17], MethodOfControl: missionData[18],
          },
        }));
      }
      await fetchShots();
      await fetchMissionState(form.mission_id);
    } catch (error) {
      console.error("Error fetching mission data:", error);
    }
  };

  const exportDatabase = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (form.mission_id) params.set("mission_id", form.mission_id);
      const response = await axios.get(`${backendUrl}/export-db?${params.toString()}`, { responseType: "blob", timeout: 30000 });
      const contentDisposition = response.headers["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = form.mission_id
        ? `fdc_export_${form.mission_id}.${format === "csv" ? "zip" : "xlsx"}`
        : `fdc_export_all.${format === "csv" ? "zip" : "xlsx"}`;
      const fileName = fileNameMatch?.[1] || fallbackName;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", fileName);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting database:", error);
      alert("Failed to export database.");
    }
  };

  const handleClearDatabase = async () => {
    const confirmation = window.confirm("Are you sure you want to clear the ENTIRE database?\nThis action is irreversible!");
    if (!confirmation) return;
    try {
      const response = await axios.post(`${backendUrl}/clear-db`);
      if (response.data.status === "success") {
        alert("Database cleared successfully!");
        localStorage.removeItem("chacal-selected-mission");
        setMissionOptions([]); setShots([]); setObservations([]); setProcessedObservations([]);
        setForm((prev) => ({ ...prev, mission_id: "", description: "", mgrs_target: "", ptl: "", target_altitude: 0, target: { x: 0, y: 0, z: 0 }, call_for_fire: {} }));
      }
    } catch (error) {
      console.error("Error clearing database:", error);
      alert("Error clearing database.");
    }
  };

  // ── Effects ──

  // Auto-open the unprocessed observations panel when an observation arrives,
  // auto-close it when the queue empties, and pop a top notification + vibrate
  // when a NEW observation arrives so the FDC is alerted even if the panel was
  // already visible.
  const prevUnprocessedCountRef = useRef(0);
  useEffect(() => {
    const prev = prevUnprocessedCountRef.current;
    const cur = observations.length;
    if (cur > prev) {
      // New observation(s) just arrived.
      setNewObservationNotification(true);
      try { navigator?.vibrate?.([180, 80, 180]); } catch { /* ignore */ }
      setExpanded((p) => ({ ...p, unproc: true }));
    } else if (prev > 0 && cur === 0) {
      // Queue cleared — fold the panel away.
      setExpanded((p) => ({ ...p, unproc: false }));
    } else if (prev === 0 && cur > 0) {
      // Edge case: panel was empty, now has one (e.g., on mission load).
      setExpanded((p) => ({ ...p, unproc: true }));
    }
    prevUnprocessedCountRef.current = cur;
  }, [observations.length]);

  useEffect(() => {
    const savedMissionId = localStorage.getItem("chacal-selected-mission");
    if (savedMissionId) {
      const savedPtl = localStorage.getItem(getMissionPtlStorageKey(savedMissionId)) ?? "";
      setForm((prev) => ({ ...prev, mission_id: savedMissionId, ptl: savedPtl }));
    }
  }, []);

  useEffect(() => {
    if (form.mission_id) localStorage.setItem("chacal-selected-mission", form.mission_id);
    else localStorage.removeItem("chacal-selected-mission");
  }, [form.mission_id]);

  useEffect(() => {
    if (!form.mission_id) return;
    localStorage.setItem(getMissionPtlStorageKey(form.mission_id), String(form.ptl ?? ""));
  }, [form.mission_id, form.ptl]);

  const registryWeapon = (name) =>
    registryWeapons.find((x) => x.name === String(name || "").toUpperCase());
  const weaponOptions = registryWeapons.length
    ? registryWeapons.map((x) => ({ value: x.name, label: x.display_name || x.name }))
    : [{ value: "M109", label: "M109" }, { value: "M114", label: "M114" }, { value: "M119", label: "M119" }];
  const chargeOptionsFor = (weapon) => {
    const wpn = registryWeapon(weapon);
    return wpn && wpn.charges && wpn.charges.length ? wpn.charges : getChargeOptions(weapon);
  };
  const projectileOptionsFor = (weapon) => {
    const wpn = registryWeapon(weapon);
    if (wpn && wpn.projectiles && wpn.projectiles.length) {
      return wpn.projectiles.map((p) => ({
        value: p.label,
        label: p.label.charAt(0) + p.label.slice(1).toLowerCase(),
      }));
    }
    return null; // null -> FireControlBar uses its built-in HE/Smoke/Illum
  };

  useEffect(() => {
    const allowed = chargeOptionsFor(form.weapon);
    if (!allowed.includes(form.charge)) setForm((prev) => ({ ...prev, charge: allowed[0] }));
  }, [form.weapon, registryWeapons]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset regulating gun when the mission changes; the next effect re-hydrates it
  // from localStorage (or falls back to a default) for the new mission.
  useEffect(() => {
    setRegulatingGunId(null);
  }, [form.mission_id]);

  // Hydrate regulating gun: prefer the user's saved choice for this mission, then
  // a sensible default. NEVER override an existing valid selection.
  useEffect(() => {
    if (!batteryOptions.length) return;
    if (regulatingGunId && batteryOptions.find((g) => g.id === regulatingGunId)) return;

    let saved = null;
    try {
      saved = form.mission_id ? localStorage.getItem(`chacal-reg-gun-${form.mission_id}`) : null;
    } catch { /* ignore */ }

    if (saved && batteryOptions.find((g) => g.id === saved)) {
      setRegulatingGunId(saved);
      return;
    }

    // Fall back to gun 3, then 4, then the first available, only when nothing was saved.
    const preferred =
      batteryOptions.find((g) => String(g.gun_number) === "3") ||
      batteryOptions.find((g) => String(g.gun_number) === "4") ||
      batteryOptions[0];
    setRegulatingGunId(preferred?.id ?? null);
  }, [batteryOptions, regulatingGunId, form.mission_id]);

  // Persist regulating gun per mission so refreshes don't reset it; ALSO push it to
  // the backend so the gun commanders can see who is currently regulating.
  useEffect(() => {
    if (!form.mission_id) return;
    try {
      if (regulatingGunId) {
        localStorage.setItem(`chacal-reg-gun-${form.mission_id}`, regulatingGunId);
      }
    } catch { /* ignore */ }

    if (!regulatingGunId) return;
    const regGun = batteryOptions.find((g) => g.id === regulatingGunId);
    if (!regGun) return;
    const regGunNumber = String(regGun.gun_number).trim();
    axios
      .post(`${backendUrl}/mission/${form.mission_id}/regulating-gun`, {
        regulating_gun_id: regGunNumber,
      })
      .catch((err) => console.error("Failed to broadcast regulating gun:", err));
  }, [regulatingGunId, form.mission_id, batteryOptions, backendUrl]);

  useEffect(() => {
    if (correctionMode !== "single" || !regulatingGunId) return;
    setSelectedObservationGuns((prev) => {
      const next = { ...prev };
      for (const obs of observations) { if (!next[obs.id] || next[obs.id].length === 0) next[obs.id] = [regulatingGunId]; }
      return next;
    });
  }, [correctionMode, regulatingGunId, observations]);

  // Seed projectile from call_for_fire when it changes (new mission), but keep user override.
  useEffect(() => {
    const cffProj = form.call_for_fire?.Projectile;
    if (!cffProj) return;
    const p = String(cffProj).toUpperCase();
    if (p.includes("ILL")) setProjectile("ILL");
    else if (p.includes("SMOKE") || p.includes("WP") || p.includes("FUM")) setProjectile("SMOKE");
    else setProjectile("HE");
  }, [form.call_for_fire?.Projectile]);

  // Smoke and ILL projectiles can only be fired with Time fuze — force it.
  useEffect(() => {
    const p = (projectile || "").toUpperCase();
    if ((p === "SMOKE" || p === "ILL") && fuzeType !== "TIME") {
      setFuzeType("TIME");
    }
  }, [projectile, fuzeType]);

  // Phase-driven gun selection:
  //  AF  — only the regulating gun is selected for the next fire (it's the one walking shots in).
  //  FFE — every gun on the mission is selected (all guns fire on the adjusted target).
  // The user can still override manually after the auto-select; we don't fight them.
  const phaseSyncedRef = useRef(null);
  useEffect(() => {
    if (!hasMission) return;
    // Only auto-sync once per phase change to avoid clobbering manual edits.
    const phaseKey = `${phase}::${committedMissionGunNumbers.join(",")}::${regulatingGunId ?? ""}`;
    if (phaseSyncedRef.current === phaseKey) return;
    phaseSyncedRef.current = phaseKey;

    if (phase === "FFE") {
      // Select every committed gun for fire.
      const committed = committedMissionGunNumbers.map(String);
      if (committed.length) setSelectedMissionGunNumbers(committed);
    } else {
      // AF: select only the regulating gun.
      const regGun = batteryOptions.find((g) => g.id === regulatingGunId);
      const regNumber = regGun ? String(regGun.gun_number) : null;
      if (regNumber) setSelectedMissionGunNumbers([regNumber]);
    }
  }, [phase, hasMission, committedMissionGunNumbers, regulatingGunId, batteryOptions]);

  // Keep activeObservationId in sync: auto-pick the newest unprocessed obs when none chosen,
  // and clear it if the chosen obs is gone (processed or removed).
  useEffect(() => {
    const ids = observations.map((o) => o.id);
    if (activeObservationId && !ids.includes(activeObservationId)) {
      setActiveObservationId(null);
      return;
    }
    if (!activeObservationId && observations.length > 0) {
      const latest = [...observations].sort((a, b) => (b.shot_id ?? 0) - (a.shot_id ?? 0))[0];
      if (latest) setActiveObservationId(latest.id);
    }
  }, [observations, activeObservationId]);

  useEffect(() => {
    if (!batteryOptions.length) return;
    pattern.setPatternPoints((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of batteryOptions) { const k = String(g.gun_number); if (!next[k]) { next[k] = { dx: 0, dy: 0 }; changed = true; } }
      return changed ? next : prev;
    });
  }, [batteryOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a mission becomes (or stops being) available, decide which panels to open.
  // Only auto-open the Mission Details panel when PTL is missing (so the operator is
  // prompted to fill it). Firing Data and Unprocessed Observations are auto-opened by
  // their own effects (below) when their respective collections become non-empty.
  useEffect(() => {
    if (!hasMission) {
      setExpanded(DEFAULT_EXPANDED);
      return;
    }
    const ptlMissing = !String(form.ptl ?? "").trim();
    setExpanded((prev) => ({
      ...DEFAULT_EXPANDED,
      selectMission: false,
      missionDetails: ptlMissing,
      // Preserve the current open state for shots / unproc — their data-driven effects
      // will toggle them appropriately as data arrives or empties.
      shots: prev.shots,
      unproc: prev.unproc,
    }));
  }, [hasMission]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open Firing Data when shots first appear; auto-close when the queue empties.
  const prevShotsCountRef = useRef(0);
  useEffect(() => {
    const prev = prevShotsCountRef.current;
    const cur = shots.length;
    if (prev === 0 && cur > 0) {
      setExpanded((p) => ({ ...p, shots: true }));
    } else if (prev > 0 && cur === 0) {
      setExpanded((p) => ({ ...p, shots: false }));
    }
    prevShotsCountRef.current = cur;
  }, [shots.length]);
  useEffect(() => { if (form.mission_id) handleFetchMissionData(); }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLatestGunPositions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Loitering-Munition (LM) missions ----------------------------------
  // The selected mission's TYPE drives the layout: when the chosen mission is an
  // LM strike we replace the artillery accordion + tools with a dedicated LM view
  // (details on the left, target/geometries on the map to the right).
  const [lmMissionIds, setLmMissionIds] = useState(() => new Set());
  const [lmDetail, setLmDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await axios.get(`${backendUrl}/lm/missions`, { timeout: 10000 });
        if (alive) setLmMissionIds(new Set((res.data?.missions || []).map((m) => m.cff_id)));
      } catch (e) { /* offline / not authed yet — leave the set as-is */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [backendUrl]);

  const isLmMission = !!form.mission_id && lmMissionIds.has(form.mission_id);

  useEffect(() => {
    if (!isLmMission) { setLmDetail(null); return undefined; }
    let alive = true;
    axios
      .get(`${backendUrl}/lm/${encodeURIComponent(form.mission_id)}`, { timeout: 12000 })
      .then((res) => { if (alive) setLmDetail(res.data?.mission || null); })
      .catch(() => { if (alive) setLmDetail(null); });
    return () => { alive = false; };
  }, [backendUrl, form.mission_id, isLmMission]);

  // Weapon registry (agnostic gun types). Falls back to static constants if the
  // endpoint is unavailable, so the FDC always offers the built-in weapons.
  useEffect(() => {
    axios.get(`${backendUrl}/weapons`).then((r) => setRegistryWeapons(r.data?.weapons || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let inFlight = false;
    const refresh = async () => { if (inFlight) return; inFlight = true; try { await fetchLatestGunPositions(); } finally { inFlight = false; } };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadMissionScopedData = async () => {
      await fetchLatestGunPositions();
      if (form.mission_id) {
        const mapped = await fetchBatteryData();
        const missionGunNumbers = Array.isArray(mapped) ? mapped.map((g) => String(g.gun_number)) : [];
        setSelectedMissionGunNumbers(missionGunNumbers); setCommittedMissionGunNumbers(missionGunNumbers);
      } else {
        setBatteryOptions([]); setSelectedMissionGunNumbers([]); setCommittedMissionGunNumbers([]);
      }
    };
    loadMissionScopedData();
  }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (form.mission_id) fetchProcessedObservations(); }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety clearances: load on mission change and refresh every 8s while a mission
  // is open, so edits made in the Safety tool reach the firing-data check quickly.
  useEffect(() => { fetchSafetyClearances(); }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!form.mission_id) return;
    const id = setInterval(fetchSafetyClearances, 8000);
    return () => clearInterval(id);
  }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.mission_id) return;
    setEffectiveTargetMgrs(""); pattern.setPatternAnchor("original"); pattern.setPatternPoints({}); pattern.setPatternError("");
    setSelectedObservationGuns({});
    fetchMissionState(form.mission_id);
  }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    refreshMissionOptions();
    const interval = setInterval(refreshMissionOptions, 12000);
    return () => clearInterval(interval);
  }, [backendUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.mission_id || loading) return;
    let inFlight = false;
    const refresh = async () => { if (inFlight) return; inFlight = true; try { await fetchObservations(); } finally { inFlight = false; } };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [form.mission_id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.mission_id) return;
    let inFlight = false;
    const refresh = async () => { if (inFlight) return; inFlight = true; try { await fetchBatteryData(); } finally { inFlight = false; } };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [form.mission_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll shots so acknowledgments from gun commanders show up live on the FDC.
  useEffect(() => {
    if (!form.mission_id || loading) return;
    let inFlight = false;
    const refresh = async () => { if (inFlight) return; inFlight = true; try { await fetchShots(); } finally { inFlight = false; } };
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [form.mission_id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!selectedShotId) return; setSelectedProcessedObservationId(null); setSelectedGunId(null); }, [selectedShotId]);
  useEffect(() => { if (!selectedProcessedObservationId) return; setSelectedShotId(null); setSelectedGunId(null); }, [selectedProcessedObservationId]);
  useEffect(() => {
    if (!selectedGunId) return;
    setSelectedShotId(null); setSelectedProcessedObservationId(null);
    if (!tools.showMapPanel) tools.openTool("map");
  }, [selectedGunId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Computed values ──
  const shotsByGun = shots.reduce((acc, shot) => { const gunKey = shot.gun_id || "N/A"; if (!acc[gunKey]) acc[gunKey] = []; acc[gunKey].push(shot); return acc; }, {});
  const sortedGunKeys = Object.keys(shotsByGun).sort((a, b) => (a === "N/A" ? 999 : Number(a)) - (b === "N/A" ? 999 : Number(b)));

  const selectionBatteryOptions = useMemo(() => {
    return latestGunPositions.map((gun) => {
      const gunNumber = String(gun.gun_number);
      return { ...gun, is_battery_center: selectedMissionGunNumbers.includes(gunNumber), is_on_mission: committedMissionGunNumbers.includes(gunNumber) };
    });
  }, [latestGunPositions, selectedMissionGunNumbers, committedMissionGunNumbers]);

  const selectedGunsForMission = selectionBatteryOptions.filter((g) => g.is_battery_center);
  const targetReferenceMgrs = form.mgrs_target;

  const observationDisplayRows = useMemo(
    () => observations.map((obs) => ({ ...obs, displayMetrics: deriveObservationMetrics(obs, targetReferenceMgrs) })),
    [observations, targetReferenceMgrs],
  );

  const processedObservationDisplayRows = useMemo(
    () => processedObservations.map((obs) => ({ ...obs, displayMetrics: deriveObservationMetrics(obs, targetReferenceMgrs) })),
    [processedObservations, targetReferenceMgrs],
  );

  // Default ~60/40 split (mission tabs / tool dock); draggable via the dock handle.
  const leftPanelWidth = !isMobile && tools.toolsOpen ? `calc(100% - ${dock.dockWidth}px - 16px)` : "100%";
  const desktopWorkspaceHeight = `calc(100vh - var(--topmodebar-height, 64px) - ${dock.stickyToolbarHeight + 24}px)`;

  // ── Render ──
  const renderToolsDock = () => (
    <ToolsDock
      toolsOpen={tools.toolsOpen}
      startResize={isMobile ? () => {} : dock.startResize}
      dockWidth={isMobile ? "100%" : dock.dockWidth}
      activeTool={tools.activeTool}
      showMapPanel={tools.showMapPanel}
      showPatternPanel={tools.showPatternPanel}
      showBatteryTool={tools.showBatteryTool}
      showFireRegTool={tools.showFireRegTool}
      showSafetyTool={tools.showSafetyTool}
      showCreateMissionTool={tools.showCreateMissionTool}
      showEditMissionTool={tools.showEditMissionTool}
      shots={shots} form={form}
      selectedShotId={selectedShotId} setSelectedShotId={setSelectedShotId}
      selectedProcessedObservationId={selectedProcessedObservationId} setSelectedProcessedObservationId={setSelectedProcessedObservationId}
      selectedGunId={selectedGunId} setSelectedGunId={setSelectedGunId}
      mapCenter={mapCenter} processedObservations={processedObservations} observations={observations}
      batteryOptions={selectionBatteryOptions}
      backendUrl={backendUrl} gunOptions={GUN_OPTIONS}
      fetchBatteryData={fetchBatteryData}
      correctionMode={correctionMode} setCorrectionMode={setCorrectionMode}
      regulatingGunId={regulatingGunId} setRegulatingGunId={setRegulatingGunId}
      patternAnchor={pattern.patternAnchor} setPatternAnchor={pattern.setPatternAnchor}
      effectiveTargetMgrs={effectiveTargetMgrs} setEffectiveTargetMgrs={setEffectiveTargetMgrs}
      patternEnabled={pattern.patternEnabled} setPatternEnabled={pattern.setPatternEnabled}
      getCenterTargetMgrs={getCenterTargetMgrs}
      patternMode={pattern.patternMode} setPatternMode={pattern.setPatternMode}
      buildCircleDistribution={pattern.buildCircleDistribution}
      buildLinearDistribution={pattern.buildLinearDistribution}
      setPatternPoints={pattern.setPatternPoints} patternPoints={pattern.patternPoints}
      patternRadius={pattern.patternRadius} setPatternRadius={pattern.setPatternRadius}
      patternRotationMils={pattern.patternRotationMils} setPatternRotationMils={pattern.setPatternRotationMils}
      linearBearingMils={pattern.linearBearingMils} setLinearBearingMils={pattern.setLinearBearingMils}
      linearSpacing={pattern.linearSpacing} setLinearSpacing={pattern.setLinearSpacing}
      linearSnapToTicks={pattern.linearSnapToTicks} setLinearSnapToTicks={pattern.setLinearSnapToTicks}
      ringStep={pattern.ringStep} setRingStep={pattern.setRingStep}
      radialStepMils={pattern.radialStepMils} setRadialStepMils={pattern.setRadialStepMils}
      showRadialLabels={pattern.showRadialLabels} setShowRadialLabels={pattern.setShowRadialLabels}
      snapEnabled={pattern.snapEnabled} setSnapEnabled={pattern.setSnapEnabled}
      snapMeters={pattern.snapMeters} setSnapMeters={pattern.setSnapMeters}
      patternError={pattern.patternError}
      createMissionDefaultZone={form.mgrs_zone || "29SND"}
      handleMissionCreated={handleMissionCreated}
      handleMissionUpdated={handleMissionUpdated}
    />
  );

  return (
    <Box sx={{ width: "100%", px: { xs: 1, sm: 1.5, md: 2 }, py: 2, pb: { xs: 12, md: 2 } }}>
      <Snackbar
        open={missionNotification} onClose={() => setMissionNotification(false)}
        message="NEW FIRE MISSION!" anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={<Button color="inherit" size="small" onClick={() => setMissionNotification(false)}>Close</Button>}
      />
      <Snackbar
        open={newObservationNotification}
        onClose={() => setNewObservationNotification(false)}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="NEW OBSERVATION RECEIVED"
        ContentProps={{
          sx: (theme) => ({
            bgcolor: theme.palette.warning.main,
            color: theme.palette.getContrastText(theme.palette.warning.main),
            fontWeight: 700,
            letterSpacing: 0.6,
          }),
        }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setNewObservationNotification(false);
              setExpanded((p) => ({ ...p, unproc: true }));
            }}
            sx={{ fontWeight: 700 }}
          >
            View
          </Button>
        }
      />

      <Box
        ref={dock.stickyToolbarRef}
        sx={(theme) => ({
          position: "sticky", top: "var(--topmodebar-height, 64px)",
          zIndex: theme.zIndex.appBar - 1, mb: 2, pt: 0.5, pb: 1,
          background: theme.palette.mode === "dark"
            ? "linear-gradient(to bottom, rgba(18,18,18,0.98), rgba(18,18,18,0.92))"
            : "linear-gradient(to bottom, rgba(255,255,255,0.98), rgba(255,255,255,0.92))",
          backdropFilter: "blur(8px)",
        })}
      >
        <Stack spacing={1}>
          {/* Mission selector + actions. On mobile we collapse Clear/Delete/Tools into
              a horizontal row of icon buttons next to the mission dropdown. On desktop
              the existing labelled buttons stay (still readable, plenty of space). */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={form.mission_id || ""} onChange={handleMissionChange} displayEmpty
                sx={{
                  borderRadius: 999,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(30,38,24,0.96)" : "rgba(249,250,244,0.96)",
                  border: "1px solid",
                  borderColor: theme.palette.mode === "dark" ? "rgba(166,176,120,0.28)" : "rgba(85,107,47,0.18)",
                  boxShadow: theme.palette.mode === "dark" ? "0 8px 20px rgba(0,0,0,0.28)" : "0 8px 20px rgba(51,61,36,0.08)",
                  fontWeight: 600,
                  "& .MuiSelect-select": { py: 1, pr: 4 },
                }}
                renderValue={(selected) => {
                  if (!selected) return "Select mission";
                  const mission = missionOptions.find((item) => item.mission_id === selected);
                  return mission ? `${mission.mission_id} - ${mission.description}` : selected;
                }}
              >
                <MenuItem value=""><em>Select mission</em></MenuItem>
                {missionOptions.map((mission) => (
                  <MenuItem key={mission.mission_id} value={mission.mission_id}>{mission.mission_id} - {mission.description}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isMobile ? (
              <>
                <Tooltip title="Clear mission selection" arrow>
                  <span>
                    <IconButton
                      size="small" color="inherit" onClick={handleClearMission} disabled={!hasMission}
                      sx={(t) => ({
                        width: 36, height: 36,
                        border: "1px solid",
                        borderColor: alpha(t.palette.divider, 0.6),
                        bgcolor: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.06 : 0.03),
                      })}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete mission permanently" arrow>
                  <span>
                    <IconButton
                      size="small" color="error" onClick={handleDeleteMission} disabled={!hasMission}
                      sx={(t) => ({
                        width: 36, height: 36,
                        border: "1px solid",
                        borderColor: alpha(t.palette.error.main, 0.5),
                        bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.1 : 0.05),
                        color: t.palette.error.main,
                      })}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={tools.toolsOpen ? `Close ${tools.activeToolLabel}` : "Open tools"} arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (tools.toolsOpen) tools.closeAllTools();
                      else tools.openTool(tools.getDefaultToolValue());
                    }}
                    sx={(t) => ({
                      width: 36, height: 36,
                      border: "1px solid",
                      borderColor: tools.toolsOpen
                        ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f")
                        : alpha(t.palette.divider, 0.6),
                      bgcolor: tools.toolsOpen
                        ? (t.palette.mode === "dark" ? "#a6b078" : "#556b2f")
                        : alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.06 : 0.03),
                      color: tools.toolsOpen
                        ? (t.palette.mode === "dark" ? "#11150f" : "#f7f7f2")
                        : "inherit",
                    })}
                  >
                    <BuildRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button variant="outlined" color="inherit" size="small" onClick={handleClearMission} disabled={!hasMission}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 600, minWidth: 120, flexShrink: 0 }}>
                  Clear
                </Button>
                <Button
                  variant="outlined" color="error" size="small"
                  onClick={handleDeleteMission} disabled={!hasMission}
                  sx={(t) => ({
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: 120,
                    flexShrink: 0,
                    borderColor: alpha(t.palette.error.main, 0.5),
                    color: t.palette.error.main,
                    "&:hover": {
                      borderColor: t.palette.error.main,
                      bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.12 : 0.06),
                    },
                  })}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>

          {/* Desktop tools toggle row — hidden on mobile because the Tools icon button
              above already opens the tools dock. Also hidden for an LM strike, whose
              layout replaces the artillery tools entirely. */}
          {!isMobile && !isLmMission && (
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, p: 0.75, borderRadius: 999,
              bgcolor: theme.palette.mode === "dark" ? "rgba(26,33,21,0.95)" : "rgba(248,249,243,0.97)",
              border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(166,176,120,0.24)" : "rgba(85,107,47,0.16)",
              boxShadow: theme.palette.mode === "dark" ? "0 10px 24px rgba(0,0,0,0.22)" : "0 10px 24px rgba(51,61,36,0.08)",
            }}>
              <ToggleButtonGroup exclusive value={tools.activeTool} onChange={tools.handleToolChange} size="small"
                sx={{
                  flex: 1, minWidth: 0,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(19,24,15,0.85)" : "rgba(238,242,231,0.95)",
                  borderRadius: 999, p: 0.5, border: "1px solid",
                  borderColor: theme.palette.mode === "dark" ? "rgba(166,176,120,0.18)" : "rgba(85,107,47,0.14)",
                  "& .MuiToggleButton-root": {
                    border: 0, borderRadius: 999, px: { xs: 1.25, sm: 1.8 }, py: 0.8,
                    textTransform: "none", fontWeight: 600, letterSpacing: 0.2, whiteSpace: "nowrap",
                    color: theme.palette.mode === "dark" ? "#c7d1a7" : "#465a27",
                    "&.Mui-selected": { color: theme.palette.mode === "dark" ? "#11150f" : "#f7f7f2", bgcolor: theme.palette.mode === "dark" ? "#a6b078" : "#556b2f" },
                    "&.Mui-selected:hover": { bgcolor: theme.palette.mode === "dark" ? "#b7c58a" : "#465a27" },
                  },
                }}>
                {tools.toolOptions.map((tool) => (
                  <ToggleButton key={tool.value} value={tool.value} disabled={tool.disabled}>{tool.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {tools.toolsOpen && (
                <Button variant="outlined" size="small" onClick={tools.closeAllTools} sx={{ ...tacticalOutlinedSx, flexShrink: 0 }}>Close</Button>
              )}
            </Box>
          )}
          {hasMission && !isLmMission && (
            <FireControlBar
              hasMission={hasMission}
              weapon={form.weapon}
              charge={form.charge}
              projectile={projectile}
              onProjectileChange={setProjectile}
              projectileOptions={projectileOptionsFor(form.weapon)}
              angle={form.angle || "la"}
              onAngleChange={(v) => setForm((p) => ({ ...p, angle: v }))}
              fuzeType={fuzeType}
              onFuzeTypeChange={setFuzeType}
              heightOfBurst={heightOfBurst}
              onHeightOfBurstChange={setHeightOfBurst}
              rounds={rounds}
              onRoundsChange={setRounds}
              phase={phase}
              onPhaseChange={setPhase}
              selectedGunsCount={selectedMissionGunNumbers.length}
              onExecuteFire={handleFire}
              ptlMissing={!String(form.ptl ?? "").trim()}
              disabled={loading}
              activeObservationId={activeObservationId}
              activeObservationShotId={observations.find((o) => o.id === activeObservationId)?.shot_id}
              onClearActiveObservation={() => setActiveObservationId(null)}
              batteryOptions={selectionBatteryOptions}
              committedGunNumbers={committedMissionGunNumbers}
              toggleGunForMission={toggleGunForMission}
              onFocusGun={focusGunTransiently}
              regulatingGunId={regulatingGunId}
              onRegulatingGunChange={setRegulatingGunId}
              handleAssignToMission={handleAssignSelectedGunsToMission}
              assigningGuns={assigningMissionGuns}
            />
          )}
        </Stack>
      </Box>

      {isLmMission ? (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch", gap: 1, width: "100%", height: { xs: "auto", md: desktopWorkspaceHeight }, minHeight: { xs: 0, md: 0 }, overflow: { xs: "visible", md: "hidden" } }}>
          <Box sx={{
            width: { xs: "100%", md: "42%" }, minWidth: 0, height: { xs: "auto", md: "100%" },
            overflowY: { xs: "visible", md: "auto" }, overflowX: "hidden", pr: { md: 1 },
          }}>
            <Box sx={{
              border: "1px solid",
              borderColor: theme.palette.mode === "dark" ? "rgba(166,176,120,0.22)" : "rgba(85,107,47,0.14)",
              borderRadius: 2, p: 2,
              bgcolor: theme.palette.mode === "dark" ? "rgba(21,27,17,0.96)" : "rgba(250,251,246,0.98)",
            }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>LM Strike (Loitering Munition)</Typography>
              {lmDetail
                ? <LmMissionPanel backendUrl={backendUrl} mission={lmDetail} />
                : <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading LM mission…</Typography>}
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, height: { xs: 480, md: "100%" }, borderRadius: 2, overflow: "hidden" }}>
            <MapComponent mapId="lm-map" lmGeometries={lmDetail?.geometries || null} />
          </Box>
        </Box>
      ) : (
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 0, width: "100%", height: { xs: "auto", md: desktopWorkspaceHeight }, minHeight: { xs: 0, md: 0 }, overflow: { xs: "visible", md: "hidden" } }}>
        <Box sx={{
          width: { xs: "100%", md: leftPanelWidth }, minWidth: 0, minHeight: { xs: 0, md: 0 },
          height: { xs: "auto", md: "100%" }, overflowY: { xs: "visible", md: "auto" }, overflowX: "hidden",
          transition: !isMobile && dock.isResizing ? "none" : "width 0.2s ease",
          pr: !isMobile && tools.toolsOpen ? 1 : 0,
        }}>
          <Box sx={{ width: "100%", maxWidth: "100%", ml: 0, mr: 0 }}>
            <MissionDetailsPanel
              expanded={expanded.missionDetails} onChange={handleExpand("missionDetails")}
              hasMission={hasMission} form={form} handleChange={handleChange} setForm={setForm}
              ptlError={ptlError} validatePtl={validatePtl} mgrsError={mgrsError}
              handleMgrsConfirm={handleMgrsConfirm} getChargeOptions={chargeOptionsFor} weaponOptions={weaponOptions} openPanel={openPanel}
              onSave={() => setExpanded((p) => ({ ...p, missionDetails: false }))}
            />
            <MetVePanel
              expanded={expanded.metVe} onChange={handleExpand("metVe")}
              hasMission={hasMission} form={form} handleChange={handleChange}
            />
            <FiringDataPanel
              expanded={expanded.shots} onChange={handleExpand("shots")} hasMission={hasMission}
              shots={shots}
              selectedShotId={selectedShotId} setSelectedShotId={setSelectedShotId}
              onConfirmShots={confirmShots} onDeleteShots={deleteShots}
              safetyClearances={safetyClearances}
            />
            <ManualObservationPanel
              expanded={expanded.manualObs} onChange={handleExpand("manualObs")}
              hasMission={hasMission} form={form} backendUrl={backendUrl} onSubmitted={handleManualObservationSubmitted}
            />
            <UnprocessedObservationsPanel
              expanded={expanded.unproc} onChange={handleExpand("unproc")} hasMission={hasMission}
              observations={observationDisplayRows} loading={loading}
              removeObservation={removeObservation}
              ptlMissing={!String(form.ptl ?? "").trim()}
              activeObservationId={activeObservationId}
              onSetActiveObservation={setActiveObservationId}
            />
            <ProcessedObservationsPanel
              expanded={expanded.proc} onChange={handleExpand("proc")} hasMission={hasMission}
              processedObservations={processedObservationDisplayRows} targetMgrs={targetReferenceMgrs}
              selectedProcessedObservationId={selectedProcessedObservationId} setSelectedProcessedObservationId={setSelectedProcessedObservationId}
              onDelete={removeObservation}
            />
            <AdminPanel expanded={expanded.admin} onChange={handleExpand("admin")} handleClearDatabase={handleClearDatabase} exportDatabase={exportDatabase} />
          </Box>
        </Box>
        {!isMobile && renderToolsDock()}
      </Box>
      )}

      {/* <ChatPanel />  hidden per user request */}

      {isMobile && (
        <Drawer anchor="bottom" open={tools.toolsOpen} onClose={tools.closeAllTools}
          PaperProps={{ sx: { height: "78vh", borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: "hidden" } }}
          ModalProps={{ keepMounted: true }}>
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
            <Box sx={{ width: 42, height: 5, borderRadius: 999, bgcolor: "text.disabled" }} />
          </Box>
          <Box sx={{ px: 1, py: 1, display: "flex", gap: 1, alignItems: "center", borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper", position: "sticky", top: 0, zIndex: 2 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <Select value={tools.activeTool || tools.getDefaultToolValue()} onChange={(e) => tools.openTool(e.target.value)} displayEmpty
                sx={{ borderRadius: 999, bgcolor: theme.palette.mode === "dark" ? "rgba(19,24,15,0.92)" : "rgba(238,242,231,0.95)", fontWeight: 700, "& .MuiSelect-select": { py: 1, pr: 4 } }}>
                {tools.toolOptions.map((tool) => (
                  <MenuItem key={tool.value} value={tool.value} disabled={tool.disabled}>{tool.mobileLabel}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" onClick={tools.closeAllTools} sx={{ ...tacticalOutlinedSx, minWidth: 0, px: 1.5 }}>Close</Button>
          </Box>
          <Box sx={{ height: "calc(78vh - 58px)", overflow: "auto", bgcolor: "background.default" }}>{renderToolsDock()}</Box>
        </Drawer>
      )}
    </Box>
  );
}
